<template>
  <div>
    <div id="profile-section" class="pentagon-background tw-bg-white">
      <Container class="!tw-max-w-[950px] tw-py-8">
        <div class="tw-text-center">
          <div class="tw-relative">
            <transition name="fade" mode="out-in" duration="100">
              <component
                :is="
                  isProfileEditable && isEditModeEnabled
                    ? EditableAvatarModal
                    : Avatar
                "
                class="tw-mx-auto"
                size="3xl"
                :src="mentor.avatarUrl"
                :alt="`${mentor.name}'s avatar`"
                :is-featured="mentor.featured"
                :is-set="mentor.hasAvatarAttached"
                shadow="xl"
                @success="refreshMentor"
              />
            </transition>
            <BaseButton
              v-if="isProfileEditable"
              color="outline-gray"
              size="small"
              class="tw-right-0 tw-top-0 tw-mt-4 md:tw-absolute md:tw-mt-0"
              @click="toggleEditMode"
            >
              <component
                :is="isEditModeEnabled ? EyeIcon : PenIcon"
                class="tw-mr-2 tw-h-[14px] tw-w-[14px]"
              />
              {{ isEditModeEnabled ? 'Public Profile' : 'Edit Profile' }}
            </BaseButton>
          </div>
          <h1 class="tw-display-2 sm:tw-display-1 tw-mt-6">
            {{ mentor.name }}
          </h1>
          <div class="tw-flex tw-items-center tw-justify-center tw-space-x-2">
            <h2 class="tw-mt-1">
              {{
                mentor.professionTitle || !isEditModeEnabled
                  ? mentor.professionTitle
                  : '-'
              }}
            </h2>
            <EditProfessionTitle
              v-if="isEditModeEnabled"
              :profession-title="mentor.professionTitle"
            />
          </div>
          <div
            class="tw-mt-4 tw-flex tw-flex-wrap tw-justify-center tw-text-sm tw-text-neutral-500"
          >
            <span
              v-if="mentor.livingInWithTimezone"
              class="tw-mb-2 tw-inline-flex tw-w-full tw-items-center tw-justify-center md:tw-mx-0 md:tw-mb-0 md:tw-mr-4 md:tw-w-max"
            >
              <LocationIcon class="tw-mr-2 tw-h-4 tw-w-4 tw-text-neutral-400" />
              {{ mentor.livingInWithTimezone }}
            </span>
            <span
              v-if="mentor.languageList.length"
              class="tw-mb-2 tw-inline-flex tw-w-full tw-items-center tw-justify-center md:tw-mx-0 md:tw-mb-0 md:tw-mr-4 md:tw-w-max"
            >
              <GlobeIcon class="tw-mr-2 tw-h-4 tw-w-4 tw-text-neutral-400" />
              {{ mentor.languageList.join(', ') }}
            </span>
            <span
              v-if="mentor.fromDetails"
              class="tw-mb-2 tw-w-full md:tw-mb-0 md:tw-mr-4 md:tw-w-max"
            >
              {{ mentor.fromDetails }}
            </span>
            <span class="tw-mb-2 tw-w-full md:tw-mb-0 md:tw-w-max">
              Joined
              {{ formatDate(mentor.createdAt, 'MMMM, yyyy') }}
            </span>
          </div>
          <div
            v-if="mentor.respondsIn"
            class="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-neutral-400"
          >
            <LightningIcon
              class="tw-mr-2 tw-h-3.5 tw-w-3.5 tw-text-success-500"
            />
            Usually responds in {{ mentor.respondsIn }}
          </div>
        </div>
        <div
          class="tw-mx-auto tw-mt-6 tw-flex tw-flex-wrap tw-space-y-4 tw-rounded-t-md tw-border tw-border-neutral-100 tw-p-4 md:tw-space-y-0"
        >
          <PricePerHour
            class="tw-w-full tw-text-center md:tw-w-44 md:tw-text-left"
            :hour-rate="mentor.hourRate"
            :hour-rate-currency="mentor.hourRateInDollars"
          />
          <AvailableSessionsBlocks
            class="tw-mx-auto tw-w-full tw-text-center md:tw-w-max"
            :is-available="mentor.available"
            :quarter-course="mentor.quarterCourse"
            :half-course="mentor.halfCourse"
            :full-course="mentor.fullCourse"
          />
          <SessionsStatistics
            class="tw-w-full tw-text-center md:tw-w-44 md:tw-text-right"
            :rating="mentor.overallRating"
            :reviews-count="mentor.reviewsForMeCount"
            :courses-count="mentor.coursesCount"
          />
        </div>
        <NextAvailable
          :is-available="mentor.available"
          :next-available-time="mentor.nextAvailableTime"
          wrapper-classes="tw-rounded-b-md tw-border-x tw-border-b tw-border-t-0 tw-rounded-t-none !tw-border-neutral-100"
        />
        <div
          class="tw-mt-6 tw-flex tw-flex-col tw-justify-between lg:tw-flex-row"
        >
          <div
            class="tw-flex tw-flex-wrap tw-items-center tw-justify-center lg:tw-justify-start lg:tw-space-x-2"
          >
            <RequestCallButton
              :is-available="mentor.available"
              :is-mentor-chargeable="mentor.chargeable"
              :mentor-slug="mentor.slug"
              class="tw-mx-2 tw-mb-2 tw-grow lg:tw-m-0"
            />
            <RequestCoachingButton
              v-if="
                hasObjectPermissions(
                  mentor,
                  OBJECT_PERMISSIONS.MENTORS.PROVIDES_COACHING,
                ) && COACHING_ENABLED
              "
              :description="mentor.coachingDescription"
              :url="profileLinks.requestMonthlyCoachingPath"
              :cost="mentor.coachingRateInDollars"
              class="tw-mx-2 tw-mb-2 tw-grow lg:tw-m-0"
            />
            <MessageMeButton
              :mentor-id="mentor.id"
              :is-disabled="isProfileEditable"
              class="tw-mx-2 tw-mb-2 tw-grow lg:tw-m-0"
            />
            <AddMentorToListButton
              :mentor-id="mentor.id"
              :mentor-added-to-lists="mentor.addedToLists"
              class="tw-mx-2 tw-mb-2 tw-grow lg:tw-m-0"
            >
              <template #button="{ openModal: openListModal }">
                <BaseButton
                  @click="openListModal"
                  color="outline-secondary"
                  is-block
                  :is-disabled="isProfileEditable"
                >
                  <UserAddIcon class="tw-mr-2 tw-h-4 tw-w-4" />
                  Add to list
                </BaseButton>
              </template>
            </AddMentorToListButton>
          </div>
          <ProfileSocialMediaSection
            :social-media="mentor.socialProfile"
            :is-edit-mode-enabled="isEditModeEnabled"
            url="/profile/mentor/social_links"
            class="tw-mt-6 tw-flex tw-items-center tw-justify-center lg:tw-mt-0 lg:tw-justify-end"
          />
        </div>
      </Container>
      <StickyElement>
        <div class="tw-border-t tw-border-neutral-100 tw-bg-white tw-shadow-sm">
          <Container class="!tw-max-w-[950px] tw-py-4 md:tw-py-2">
            <div
              class="tw-flex tw-flex-wrap tw-justify-center lg:tw-justify-between"
            >
              <div
                class="tw-flex tw-flex-col tw-space-y-2 md:tw-flex-row md:tw-space-x-6 md:tw-space-y-0"
              >
                <a
                  href="#profile-section"
                  class="tw-link"
                  data-turbolinks="false"
                >
                  Profile
                </a>
                <a
                  v-if="hasAnyContent"
                  href="#content-section"
                  class="tw-link"
                  data-turbolinks="false"
                >
                  Content Library
                </a>
                <a
                  v-if="specialties.length"
                  href="#expertise-section"
                  class="tw-link"
                  data-turbolinks="false"
                >
                  Expertise
                </a>
                <a
                  v-if="mentor.softwareSpecialties.length"
                  href="#toolkit-section"
                  class="tw-link"
                  data-turbolinks="false"
                >
                  Toolkit
                </a>
                <a
                  v-if="mentor.reviewsForMeCount"
                  href="#reviews-section"
                  class="tw-link"
                  data-turbolinks="false"
                >
                  Reviews ({{ mentor.reviewsForMeCount }})
                </a>
              </div>
              <div
                class="tw-mt-2 tw-w-full tw-text-center md:tw-ml-6 md:tw-mt-0 md:tw-w-max md:tw-text-right lg:tw-ml-0"
              >
                <CopyProfileLinkButton />
              </div>
            </div>
          </Container>
        </div>
      </StickyElement>
    </div>
    <Container class="!tw-max-w-[950px] tw-py-8">
      <div class="tw-flex tw-flex-col tw-space-y-4">
        <UserCompleteProfile
          v-if="
            isProfileEditable && isProfileStatus(PROFILE_STATUS.NOT_COMPLETE)
          "
        />
        <section
          v-if="mentor.favoritedReviews.length"
          class="tw-mb-8 tw-grid tw-gap-4"
          :class="pinnedReviewsClasses"
        >
          <PinnedReview
            v-for="review in mentor.favoritedReviews"
            :key="review.id"
            :review="review"
          />
        </section>
        <BiographySection
          :biography="mentor.extendedSummary"
          :about-me="mentor.aboutMe"
        />
        <ShortsSection
          v-if="mentorShorts.length > 0"
          :mentor-shorts="mentorShorts"
        />
        <MentorContentLibrarySection
          v-if="hasAnyContent || isEditModeEnabled || isProfileEditable"
          :has-any-content="hasAnyContent"
        />
        <ExpertiseSection
          v-if="specialties.length || isEditModeEnabled"
          :specialties="specialties"
        />
        <ToolkitSection
          v-if="mentor.softwareSpecialties.length || isEditModeEnabled"
          :toolkit="softwareSpecialties"
        />
        <IndustriesSection
          v-if="industries.length || isEditModeEnabled"
          :industries="industries"
        />
        <ExperienceSection
          v-if="experience.length || isEditModeEnabled"
          :experience="experience"
        />
        <WrittenContentSection
          v-if="links.length || isEditModeEnabled"
          :links="links"
        />
        <ReviewsSection
          v-if="mentor.reviewsForMeCount"
          :count="mentor.reviewsForMeCount"
        />
      </div>
    </Container>

    <UnlockFullAccessModal v-if="!isUserLoggedIn" />
  </div>
</template>

<script setup>
import StickyElement from 'vue-sticky-element/vue'
import Container from '@/shared/components/Container.vue'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import PricePerHour from '@/modules/growthmentor/mentors/components/PricePerHour.vue'
import AvailableSessionsBlocks from '@/modules/growthmentor/mentors/components/AvailableSessionsBlocks.vue'
import SessionsStatistics from '@/modules/growthmentor/mentors/components/SessionsStatistics.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import CopyProfileLinkButton from '@/shared/components/CopyProfileLinkButton.vue'
import NextAvailable from '@/modules/growthmentor/mentors/components/NextAvailable.vue'
import EditableAvatarModal from '@/shared/components/modals/EditableAvatarModal.vue'
import RequestCoachingButton from '@/modules/growthmentor/mentors/components/RequestCoachingButton.vue'
import RequestCallButton from '@/shared/components/RequestCallButton.vue'
import BiographySection from '@/modules/growthmentor/mentors/components/biography/BiographySection.vue'
import ExpertiseSection from '@/modules/growthmentor/mentors/components/expertise/ExpertiseSection.vue'
import IndustriesSection from '@/modules/growthmentor/mentors/components/industries/IndustriesSection.vue'
import ToolkitSection from '@/modules/growthmentor/mentors/components/toolkit/ToolkitSection.vue'
import MentorContentLibrarySection from '@/modules/growthmentor/mentors/components/content-library/MentorContentLibrarySection.vue'
import WrittenContentSection from '@/modules/growthmentor/mentors/components/written-content/WrittenContentSection.vue'
import ReviewsSection from '@/modules/growthmentor/mentors/components/reviews/ReviewsSection.vue'
import ExperienceSection from '@/modules/growthmentor/mentors/components/experience/ExperienceSection.vue'
import PinnedReview from '@/modules/growthmentor/mentors/components/reviews/PinnedReview.vue'
import MessageMeButton from '@/modules/growthmentor/mentors/components/MessageMeButton.vue'
import EditProfessionTitle from '@/modules/growthmentor/mentors/components/EditProfessionTitle.vue'
import EyeIcon from '@/assets/images/icons/eye.svg'
import PenIcon from '@/assets/images/icons/pen.svg'
import LocationIcon from '@/assets/images/icons/location.svg'
import GlobeIcon from '@/assets/images/icons/globe.svg'
import UnlockFullAccessModal from '@/shared/components/modals/UnlockFullAccessModal.vue'
import AddMentorToListButton from '@/shared/components/AddMentorToListButton.vue'
import UserAddIcon from '@/assets/images/icons/user-add.svg'
import ProfileSocialMediaSection from '@/shared/components/ProfileSocialMediaSection.vue'
import UserCompleteProfile from '@/shared/components/UserCompleteProfile.vue'
import LightningIcon from '@/assets/images/icons/lightning.svg'
import { computed, onMounted, provide, ref } from 'vue'
import { useEditProfileModeProvider } from '@/shared/composables/edit-profile-mode/useEditProfileModeProvider.js'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { queryParameters } from '@/shared/utils/queryParameters.js'
import { useDebugger } from '@/shared/composables/useDebugger.js'
import {
  ACCESS_TYPE,
  OBJECT_PERMISSIONS,
  PROFILE_STATUS,
} from '@/shared/data/permissionSystem.js'
import { router } from '@inertiajs/vue3'
import { useModals } from '@/shared/composables/useModals.js'
import { useDateManager } from '@/shared/composables/useDateManager.js'
import { useFeatureFlags } from '@/shared/composables/useFeatureFlags.js'
import ShortsSection from '@/modules/growthmentor/mentors/components/shorts/ShortsSection.vue'

const props = defineProps({
  coachingRequested: {
    type: Boolean,
    required: true,
  },
  mentor: {
    type: Object,
    default: () => ({}),
  },
  profileLinks: {
    type: Object,
    default: () => ({}),
  },
  isFreeCoursesLimitHit: {
    type: Boolean,
    required: true,
  },
  mentorColumns: {
    type: Array,
    required: true,
  },
  videos: {
    type: Array,
    required: true,
  },
  podcasts: {
    type: Array,
    required: true,
  },
  glossaries: {
    type: Array,
    required: true,
  },
  mentorShorts: {
    type: Array,
    required: true,
  },
})

const { isEditModeEnabled, enableEditMode, toggleEditMode } =
  useEditProfileModeProvider()
const { hasObjectPermissions, hasAccess, isProfileStatus, isUserLoggedIn } =
  usePermissionSystem()
const { $debug } = useDebugger()
const { openModal } = useModals()
const { formatDate } = useDateManager()
const { COACHING_ENABLED } = useFeatureFlags()

const links = computed(() => props.mentor.links)
const softwareSpecialties = computed(() => props.mentor.softwareSpecialties)
const specialties = computed(() => props.mentor.specialties)
const experience = computed(() =>
  isEditModeEnabled.value
    ? props.mentor.workExperiences
    : props.mentor.workExperiences.filter((experience) => experience.visible),
)
const industries = computed(() => {
  const industries = props.mentor.industrySpecialties
  if (isEditModeEnabled.value) return industries
  return industries.filter((industry) => !!industry.description)
})
const hasAnyContent = computed(() => {
  return (
    props.mentorColumns.length > 0 ||
    props.videos.length > 0 ||
    props.podcasts.length > 0 ||
    props.glossaries.length > 0
  )
})
const pinnedReviewsClasses = computed(() => {
  if (props.mentor.favoritedReviews.length % 2 === 0) {
    return 'tw-grid-cols-1 md:tw-grid-cols-2'
  }
  if (props.mentor.favoritedReviews.length % 3 === 0) {
    return 'tw-grid-cols-1 lg:tw-grid-cols-3'
  }
  return 'tw-grid-cols-1'
})
const isProfileEditable = computed(() => {
  return hasObjectPermissions(props.mentor, OBJECT_PERMISSIONS.MENTORS.OWNER)
})

const refreshMentor = () => {
  router.reload({ only: ['mentor', 'currentUser'] })
}

onMounted(() => {
  const openModalNameQueryParam = queryParameters().getParam('open-modal-name')
  if (openModalNameQueryParam) {
    $debug(`Opening modal: ${openModalNameQueryParam}`)
    enableEditMode()
    // We have to open modal after 500ms because of animation delay
    setTimeout(() => {
      openModal(openModalNameQueryParam)
    }, 500)
  }
})

const reviews = ref({
  total: 0,
  offset: 0,
  data: [],
})
const setReviews = ({ total, offset, data }) => {
  reviews.value.total = total
  reviews.value.offset += offset
  reviews.value.data = [...reviews.value.data, ...data]
}
const removeReviewFromFavorites = (reviewId) => {
  const review = reviews.value.data.find((review) => review.id === reviewId)
  review.permissions = review.permissions.filter(
    (permission) => permission !== OBJECT_PERMISSIONS.REVIEWS.FAVORITED,
  )
}
const addReviewToFavorites = (reviewId) => {
  const review = reviews.value.data.find((review) => review.id === reviewId)
  review.permissions = [
    ...review.permissions,
    OBJECT_PERMISSIONS.REVIEWS.FAVORITED,
  ]
}
provide('reviews', reviews)
provide('setReviews', setReviews)
provide('removeReviewFromFavorites', removeReviewFromFavorites)
provide('addReviewToFavorites', addReviewToFavorites)
</script>
